import { redirect } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/auth';
import { hasPlaceholderPermission } from '@/lib/permissions';

/**
 * Shared `beforeLoad` guard for the authenticated landing routes (`/` and `/dashboard`):
 * initialises the auth store on first load, redirects unauthenticated users to `/login`
 * and placeholder-only users to `/placeholder`, and otherwise returns so the route may
 * render. Kept in one place so the two routes' security-relevant redirect chains can't
 * drift apart.
 */
export async function requireAuthenticatedUser(): Promise<void> {
  const { isAuthenticated, isInitialized, user, initialize } = useAuthStore.getState();

  // If not initialized, wait for initialization
  if (!isInitialized) {
    await initialize();
    const state = useAuthStore.getState();
    if (!state.isAuthenticated && !state.user) {
      throw redirect({ to: '/login' });
    }
  } else {
    // Check if user exists (from persisted state) or isAuthenticated
    if (!isAuthenticated && !user) {
      throw redirect({ to: '/login' });
    }
  }

  const currentUser = useAuthStore.getState().user;
  if (hasPlaceholderPermission(currentUser)) {
    throw redirect({ to: '/placeholder' });
  }
}
